// La shell desktop (LexSystem.exe, WebView2) inietta prima di ogni documento l'indirizzo e il token
// delle API locali: { url, token, version }. Nel browser puro manca, e le funzioni locali restano spente.
export const localShell = (typeof window !== 'undefined' && window.__lexLocal) || null

// Configurazione URL delle API
const API_CONFIG = {
  localBackend: localShell?.url || import.meta.env.VITE_API_LOCAL_BACKEND || 'http://localhost:5034',
  backend: import.meta.env.VITE_API_BACKEND || '' // Empty string usa path relativo (stesso dominio)
}

// La connessione dell'hub, quando c'e'. Registrata da liveConnection.js invece di essere importata
// per non creare un ciclo fra i due moduli: quello dell'hub ha gia' bisogno di questo.
let connectionIdProvider = null

/** Chiamata da liveConnection.js all'avvio: da qui in poi ogni scrittura porta la sua origine. */
export function setConnectionIdProvider(fn) {
  connectionIdProvider = fn
}

// Chi ha appena scritto ha gia' la risposta HTTP: il Backend usa questo header per non rimandargli
// l'eco della propria modifica, che gli sovrascriverebbe il form ancora aperto.
function liveHeader(headers) {
  try {
    const id = connectionIdProvider?.()
    if (id) headers['X-Live-Connection'] = id
  } catch {
    // Una connessione non ancora pronta non deve impedire la chiamata.
  }
  return headers
}

// Funzione principale per le chiamate API
export async function apiCall(endpoint, options = {}) {
  const baseUrl = options.backend === 'local' ? API_CONFIG.localBackend : API_CONFIG.backend
  const url = `${baseUrl}${endpoint}`
  
  // Headers di default
  const defaultHeaders = liveHeader({
    'Content-Type': 'application/json',
    ...options.headers
  })
  
  // Aggiungi automaticamente il token JWT se presente e non disabilitato
  if (options.includeAuth !== false) {
    const token = localStorage.getItem('auth_token')
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`
    }
  }

  // Le API locali accettano solo le richieste che portano il token della shell
  if (options.backend === 'local' && localShell?.token) {
    defaultHeaders['X-Local-Token'] = localShell.token
  }
  
  const defaultOptions = {
    headers: defaultHeaders,
    ...options
  }
  
  try {
    const response = await fetch(url, defaultOptions)
    
    // Se la richiesta fallisce con 401 (token scaduto) e non è già una richiesta di login/refresh.
    // Le API locali non c'entrano con il JWT: un loro rifiuto non deve mandare al login.
    if (response.status === 401 && options.backend !== 'local'
        && !endpoint.includes('/auth/login') && !endpoint.includes('/auth/refresh')) {
      // Prova a rinnovare il token
      const refreshResult = await refreshToken()
      if (refreshResult.success) {
        // Riprova la richiesta originale con il nuovo token
        defaultHeaders['Authorization'] = `Bearer ${refreshResult.token}`
        const retryResponse = await fetch(url, { ...defaultOptions, headers: defaultHeaders })
        if (!retryResponse.ok) {
          const errorData = await retryResponse.json().catch(() => ({}))
          throw new Error(`HTTP error! status: ${retryResponse.status}, message: ${errorData.message || JSON.stringify(errorData)}`)
        }
        return await retryResponse.json()
      } else {
        // Se il refresh fallisce, redirect al login
        window.location.href = '/login'
        throw new Error('Session expired, please login again')
      }
    }
    
    if (!response.ok) {
      // Prova a leggere il corpo della risposta per ottenere dettagli dell'errore
      const errorData = await response.json().catch(() => ({}))
      
      // Create error object with additional information
      const error = new Error(errorData.message || `HTTP error! status: ${response.status}`)
      error.response = {
        status: response.status,
        data: errorData
      }
      error.error = errorData.error
      
      throw error
    }
    return await response.json()
  } catch (error) {
    console.error('API call failed:', error)
    throw error
  }
}

// Chiamata in streaming (SSE) verso il Backend: POST con body JSON, risposta `text/event-stream`.
// Per ogni frame `data: {...}` chiama `onEvent(evt)`. Usata dal monitoraggio live delle verifiche AI.
// Nota: EventSource nativo non fa POST né header custom → si usa fetch + ReadableStream.
// Il refresh automatico del token su 401 non è gestito qui (lo stream è un'operazione one-shot).
export async function streamCall(endpoint, body, onEvent, { signal } = {}) {
  const url = `${API_CONFIG.backend}${endpoint}`
  const headers = liveHeader({ 'Content-Type': 'application/json' })
  const token = localStorage.getItem('auth_token')
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal
  })

  if (!response.ok || !response.body) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  // Un frame SSE è separato da una riga vuota; ogni riga utile inizia con "data: ".
  const flushFrames = () => {
    let sep
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      for (const line of frame.split('\n')) {
        if (!line.startsWith('data:')) continue
        const payload = line.slice(5).trim()
        if (!payload) continue
        try {
          onEvent(JSON.parse(payload))
        } catch {
          // frame parziale/malformato: ignora
        }
      }
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    flushFrames()
  }
  // eventuale coda senza il doppio newline finale
  buffer += '\n\n'
  flushFrames()
}

/**
 * Un token buono per aprire una connessione. L'hub chiede il token a ogni negoziazione E a ogni
 * riconnessione: dopo una caduta lunga quello in memoria puo' essere gia' scaduto, e l'handshake
 * verrebbe rifiutato senza che nulla lo segnali.
 *
 * Rinnova solo all'ultimo momento (meno di due minuti alla scadenza): un refresh per ogni tentativo
 * di riconnessione sarebbe una tempesta di chiamate proprio mentre la rete va male.
 */
export async function ensureFreshToken() {
  const token = localStorage.getItem('auth_token')
  if (!token) return ''

  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const { exp } = JSON.parse(atob(payload))
    if (exp * 1000 - Date.now() > 120000) return token
  } catch {
    return token // token illeggibile: lo decide il Backend, non noi
  }

  const result = await refreshToken()
  return result.success ? result.token : ''
}

// Funzione per rinnovare il token
async function refreshToken() {
  try {
    const currentToken = localStorage.getItem('auth_token')
    if (!currentToken) {
      return { success: false }
    }
    
    const response = await fetch(`${API_CONFIG.backend}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      localStorage.setItem('auth_token', data.token)
      return { success: true, token: data.token }
    } else {
      localStorage.removeItem('auth_token')
      return { success: false }
    }
  } catch (error) {
    console.error('Token refresh failed:', error)
    localStorage.removeItem('auth_token')
    return { success: false }
  }
}

// API specifiche per autenticazione
export const authAPI = {
  login: (credentials) => apiCall('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
    includeAuth: false
  }),
  
  verify: () => apiCall('/api/auth/verify', {
    method: 'POST'
  }),
  
  refresh: () => apiCall('/api/auth/refresh', {
    method: 'POST'
  })
}

// Esporta anche la configurazione per facilità di accesso
export { API_CONFIG }